/**
 * Vertical dial: a wall plane, described by its declination from due
 * south (0 = direct south, +d = the wall's outward face has rotated
 * toward the west, so it "declines" west of south; -d toward the east).
 * This one convention, plus the general 3D projector, covers direct and
 * declining walls in both hemispheres without a separate formula for
 * each case -- a southern-hemisphere dial simply comes out with a
 * negative style height relationship handled by the same vector math.
 *
 * The reference (u) axis is straight up the wall (the plumb/vertical
 * line), which is the conventional noon-ish line for direct dials; for a
 * declining dial the noon line itself tilts away from vertical by the
 * substyle-adjacent amount, which the renderer reads off `gnomon.substyleAngleDeg`.
 *
 * Closed-form check used in the oracle test, direct south case only
 * (Waugh ch. 4): tan(theta) = cos(phi) * tan(H), i.e. a direct vertical
 * south dial is a horizontal dial for the co-latitude (90 - phi).
 */

import type { TimeReference } from "../time-reference.ts";
import { computeGnomon, generateHourLines } from "./common.ts";
import type { HourLineOptions } from "./common.ts";
import { generateDeclinationLines, STANDARD_DECLINATION_LINES, ZODIAC_DECLINATIONS } from "./declination-lines.ts";
import { generateHistoricalHours } from "./historical-hours.ts";
import { buildDialPlane } from "./plane.ts";
import type { PolarDialResult } from "./types.ts";

export interface VerticalDialOptions {
  latitudeDeg: number;
  /** Wall declination from due south, degrees; 0 = direct south, +90 = direct west. */
  wallDeclinationDeg: number;
  timeReference: TimeReference;
  hourLineOptions: HourLineOptions;
  nodusDistanceMm: number;
  /** Maximum base or rise, extended only if needed to reach the nodus. */
  gnomonBaseLengthMm: number;
  includeZodiac?: boolean;
  historicalHours?: Array<"temporal" | "babylonian" | "italian">;
}

export function buildVerticalDial(opts: VerticalDialOptions): PolarDialResult {
  const plane = buildDialPlane("vertical", opts.latitudeDeg, opts.wallDeclinationDeg); // u = straight up the wall

  const hourLines = generateHourLines(opts.latitudeDeg, plane, opts.timeReference, opts.hourLineOptions);
  const declinationSpecs = opts.includeZodiac
    ? [...STANDARD_DECLINATION_LINES, ...ZODIAC_DECLINATIONS.map((z) => ({ label: z.name, kind: "zodiac", declinationDeg: z.declinationDeg }))]
    : STANDARD_DECLINATION_LINES;
  const declinationLines = generateDeclinationLines(opts.latitudeDeg, plane, opts.nodusDistanceMm, declinationSpecs);
  const historicalHours = opts.historicalHours?.length
    ? generateHistoricalHours(opts.latitudeDeg, plane, opts.nodusDistanceMm, opts.historicalHours)
    : [];
  const gnomon = computeGnomon(opts.latitudeDeg, plane, opts.gnomonBaseLengthMm, opts.nodusDistanceMm);

  const kindLabel = opts.wallDeclinationDeg === 0 ? "direct south" : `declining ${opts.wallDeclinationDeg.toFixed(0)}° from south`;
  return {
    kind: "vertical",
    hourLines,
    declinationLines,
    historicalHours,
    gnomon,
    orientation: `Mount vertically, wall ${kindLabel}, top of the design upward. The style projects out from the wall at ${gnomon.styleHeightDeg.toFixed(1)}°. Its base runs ${((gnomon.substyleAngleDeg + 360) % 360).toFixed(1)}° clockwise from the upward plumb line as viewed from the front.`,
  };
}
